//Lets create a log chart widget that shows the number of logs generated 
//over the last 72 hours
//The chart should show the number of logs generated in 1 hour intervals

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Area
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

const parseTimestamp = (timestamp) => {
    try {
        if (timestamp.endsWith('Z') || timestamp.includes('+')) {
            return parseISO(timestamp);
        }
        
        const [date, time] = timestamp.split('-');
        if (!date || !time) {
            return new Date();
        }
        
        const [year, month, day] = date.split('-');
        const [hour, minute] = time.split('-');
        
        if (!year || !month || !day || !hour || !minute) {
            return new Date();
        }
        
        return new Date(Date.UTC(year, month - 1, day, hour, minute));
    } catch {
        return new Date();
    }
};

export default function Logs() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [timezone, setTimezone] = useState('UTC');
    const lastTimestampRef = useRef(null);

    const fetchLogs = useCallback(async () => {
        try {
            const params = lastTimestampRef.current ? { lastTimestamp: lastTimestampRef.current } : {};
            const response = await axios.get('/int/logchart', { params });
            const { data, metadata } = response.data;
            
            setTimezone(metadata.timezone || 'UTC');
            
            const validData = data.filter(log => {
                if (!log.timestamp || !log.count) {
                    return false;
                }
                
                const date = parseTimestamp(log.timestamp);
                const isValid = !isNaN(date.getTime());
                
                if (!isValid) {
                    console.error('Invalid date:', log.timestamp);
                }
                
                return isValid;
            });

            if (validData.length > 0) {
                const sortedData = validData.sort((a, b) => {
                    const dateA = parseTimestamp(a.timestamp);
                    const dateB = parseTimestamp(b.timestamp);
                    return dateA - dateB;
                });

                setLogs(sortedData);
                
                if (metadata?.lastTimestamp) {
                    lastTimestampRef.current = metadata.lastTimestamp;
                }
            }
            
        } catch (error) {
            console.error('Error fetching logs:', error);
            setError(error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchLogs();
        const interval = setInterval(fetchLogs, 60000);
        return () => clearInterval(interval);
    }, [fetchLogs]);

    const chartData = logs.map(log => {
        const utcDate = parseTimestamp(log.timestamp);
        const zonedDate = toZonedTime(utcDate, timezone);
        const formattedTime = format(zonedDate, 'HH:mm');
        const count = Number(log.count) || 0;
        
        return {
            time: formattedTime,
            count: count,
            timestamp: utcDate.getTime(),
            originalTime: log.timestamp
        };
    }).sort((a, b) => b.timestamp - a.timestamp);

    if (loading && logs.length === 0) {
        return (
            <div style={{ 
                width: '100%', 
                height: 400, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
            }}>
                Loading...
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ 
                width: '100%', 
                height: 400, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                color: 'red'
            }}>
                Error: {error.message}
            </div>
        );
    }

    return (
        <div style={{ width: '100%', height: 400, padding: '25px' }}>
            <ResponsiveContainer width="100%" height={350}>
                <LineChart 
                    data={chartData} 
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis 
                        dataKey="time" 
                        tick={{ fontSize: 12 }}
                        interval="preserveEnd"
                        minTickGap={30}
                        height={50}
                        reversed={false}
                    />
                    <YAxis 
                        tick={{ fontSize: 12 }}
                        allowDecimals={false}
                        width={40}
                        domain={[0, 'auto']}
                    />
                    <Tooltip
                        contentStyle={{
                            backgroundColor: '#fff',
                            border: '1px solid #ccc',
                            borderRadius: '4px'
                        }}
                        formatter={(value) => [value, 'Logs']}
                        labelFormatter={(label) => `Time: ${label}`}
                    />
                    <Line
                        type="monotone"
                        dataKey="count"
                        stroke="#ABBB7D"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                    />
                    <Area
                        type="linear"
                        dataKey="count"
                        fill="#D1D7B0"
                        fillOpacity={0.2}
                        stroke="none"
                    />
                </LineChart>
            </ResponsiveContainer>
            <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                alignItems: 'center',
                marginTop: '10px'
            }}>
                <div style={{ 
                    fontSize: '0.8em', 
                    color: '#666',
                    display: 'flex',
                    textAlign: 'right',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <span>Last updated: {format(new Date(), 'HH:mm:ss')}</span>
                </div>
            </div>
        </div>
    );
}